<!-- lib/components/Footer.svelte -->
<script>
  import Icon from "lib/components/Icon.svelte";
  import { _ } from "svelte-i18n";
  import {
    TELEGRAM_URL,
    TWITTER_URL,
    LINKEDIN_URL,
    FACEBOOK_URL,
    PRIVACY_POLICY,
    TERMS_AND_CONDITIONS,
  } from "utils/menu.js";

  const currentYear = new Date().getFullYear();

  $: socialLinks = [
    { icon: "social-x", url: TWITTER_URL, label: $_("footer.social.twitter") },
    {
      icon: "social-linkedin",
      url: LINKEDIN_URL,
      label: $_("footer.social.linkedin"),
    },
    {
      icon: "social-telegram",
      url: TELEGRAM_URL,
      label: $_("footer.social.telegram"),
    },
    {
      icon: "social-facebook",
      url: FACEBOOK_URL,
      label: $_("footer.social.facebook"),
    },
  ];

  $: footerLinks = [
    { text: $_("footer.links.terms"), url: TERMS_AND_CONDITIONS },
    { text: $_("footer.links.privacy"), url: PRIVACY_POLICY },
  ];

  const abn = "ABN 71 674 454 776";
  const companyName = "Liquid Noble Pty Ltd";
</script>

<footer class="footer">
  <div class="footer-content">
    <!-- Logo and Social Links -->
    <div class="footer-header">
      <div class="footer-logo">
        <img
          src="../../images/v2/LQN_logo_round.svg"
          alt={$_("footer.logo.alt")}
          width="32"
          height="32"
        />
        Liquid Noble
      </div>
      <div class="social-links">
        {#each socialLinks as link}
          <a
            href={link.url}
            class="social-link"
            aria-label={link.label}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon name={link.icon} />
          </a>
        {/each}
      </div>
    </div>

    <!-- Disclaimer -->
    <div class="disclaimer" data-nosnippet>
      <p>
        {$_("footer.disclaimer")}
      </p>
    </div>

    <!-- Footer Bottom -->
    <div class="footer-bottom">
      <div class="company-info">
        <a
          href="https://abr.business.gov.au/ABN/View?id=71674454776"
          target="_blank"
          rel="noopener noreferrer"
        >
          <p class="abn">
            {abn}
          </p>
        </a>
        <p class="copyright">
          {$_("footer.copyright", {
            values: { year: currentYear, company: companyName },
          })}
        </p>
      </div>
      <div class="footer-links">
        {#each footerLinks as link}
          <a href={link.url} class="footer-link">{link.text}</a>
        {/each}
      </div>
    </div>
  </div>
</footer>

<style lang="scss">
  .footer {
    background: linear-gradient(135deg, #0a0208 0%, #1a0914 50%, #0f0409 100%);
    border-top: 1px solid #260d28;
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(210, 165, 82, 0.3) 50%,
        transparent 100%
      );
    }
  }

  .footer-content {
    max-width: var(--max-width);
    margin: 0 auto;
    padding: 3rem 4rem;
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
  }

  .footer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 2rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }

  .footer-logo {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-size: 1.25rem;
    font-weight: 700;
    background: linear-gradient(
      120deg,
      #c6a858 20%,
      #fceabb 40%,
      #dbbb6b 50%,
      #fceabb 60%,
      #c6a858 80%
    );
    background-size: 200% auto;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: goldShine 5s ease-in-out infinite;
  }

  @keyframes goldShine {
    0% {
      background-position: -200% 0;
    }
    60% {
      background-position: 200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  .social-links {
    display: flex;
    gap: 1rem;
  }

  .social-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: var(--text-color3);
    text-decoration: none;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(210, 165, 82, 0.2);
      border-color: rgba(210, 165, 82, 0.4);
      color: var(--text-color1);
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(210, 165, 82, 0.2);
    }

    :global(svg) {
      width: 20px;
      height: 20px;
    }

    &:hover :global(svg g path) {
      fill-opacity: 0.8;
    }
  }

  .disclaimer {
    padding: 2rem;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    backdrop-filter: blur(10px);

    p {
      font-size: 0.85rem;
      line-height: 1.6;
      color: rgba(255, 255, 255, 0.7);
      margin: 0;
      text-align: justify;
      white-space: pre-line;
    }
  }

  .footer-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 1.5rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .company-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .abn {
    font-size: 0.9rem;
    color: var(--text-color3);
    margin: 0;
    font-weight: 500;
  }

  .copyright {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
  }

  .footer-links {
    display: flex;
    gap: 2rem;
  }

  .footer-link {
    color: var(--text-color3);
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.3s ease;
    position: relative;

    &:hover {
      color: var(--text-color1);
    }

    &::after {
      content: "";
      position: absolute;
      bottom: -4px;
      left: 0;
      width: 0;
      height: 2px;
      background: var(--text-color1);
      transition: width 0.3s ease;
    }

    &:hover::after {
      width: 100%;
    }
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    .footer-content {
      padding: 2rem;
      gap: 2rem;
    }

    .footer-header {
      flex-direction: column;
      gap: 1.5rem;
      text-align: center;
    }

    .footer-bottom {
      flex-direction: column;
      gap: 1.5rem;
      text-align: center;
    }

    .footer-links {
      justify-content: center;
    }

    .social-links {
      justify-content: center;
    }

    .disclaimer {
      padding: 1.5rem;
    }

    .disclaimer p {
      text-align: left;
    }
  }

  @media (max-width: 480px) {
    .footer-content {
      padding: 1.5rem;
    }

    .footer-logo {
      font-size: 1.1rem;
    }

    .social-links {
      gap: 0.75rem;
    }

    .social-link {
      width: 36px;
      height: 36px;
      font-size: 1rem;
    }

    .footer-links {
      flex-direction: column;
      gap: 1rem;
    }

    .disclaimer {
      padding: 1rem;
    }

    .disclaimer p {
      font-size: 0.8rem;
    }
  }

  @media (max-width: 320px) {
    .footer-content {
      padding: 1rem;
    }

    .footer-logo {
      font-size: 1rem;
      gap: 0.5rem;
    }

    .footer-logo img {
      width: 28px;
      height: 28px;
    }

    .social-link {
      width: 32px;
      height: 32px;
      font-size: 0.9rem;
    }
  }
</style>
